// Suggested edit cards, apply/undo edits, and document modification.

import {
  documentId,
  cachedTree,
  appliedEdits,
  activeNodeMap,
  setCachedTree,
  setLastScan,
  saveToSettings,
} from "./state";
import { getDocumentTree } from "../core/apiClient";
import { scanDocument, applyAndDiffToActive } from "../core/documentScanner";
import { flushToBackend } from "./init";
import { _buildClauseMap, renderClauseTree } from "./clauseLinks";
import { locateByNodeId } from "./selection";
import { devLog } from "./messages";

function _truncate(str, max) {
  if (!str) return "";
  return str.length > max ? str.slice(0, max) + "…" : str;
}

function _escHtml(str) {
  return str.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");
}

// Render edit cards below AI response with diff view + accept/reject buttons
export function renderSuggestedEdits(aiMsgEl, edits) {
  if (!edits || edits.length === 0) return;

  const clauseMap = _buildClauseMap(cachedTree || []);

  const container = document.createElement("div");
  container.className = "suggested-edits-container";

  const heading = document.createElement("div");
  heading.className = "suggested-edits-heading";
  heading.innerHTML = `<span>✏️ ${edits.length} Suggested Change${edits.length > 1 ? "s" : ""}</span>`;
  container.appendChild(heading);

  edits.forEach((edit, idx) => {
    const card = document.createElement("div");
    card.className = "edit-card";
    card.dataset.editIndex = idx;

    const header = document.createElement("div");
    header.className = "edit-card-header";

    const numLabel = document.createElement("span");
    numLabel.className = "edit-card-num";
    numLabel.textContent = `Change ${idx + 1}`;
    header.appendChild(numLabel);

    if (edit.node_id && clauseMap[edit.node_id]) {
      const clause = clauseMap[edit.node_id];
      const locTag = document.createElement("span");
      locTag.className = "edit-card-location";
      locTag.textContent =
        `${clause.number || ""}${clause.title ? " — " + clause.title : ""}`.trim();
      locTag.title = "Click to locate in document";
      locTag.style.cursor = "pointer";
      locTag.addEventListener("click", async () => {
        if (clause.node_ids && clause.node_ids.length > 0) {
          await locateByNodeId(clause.node_ids[0]);
        }
      });
      header.appendChild(locTag);
    }
    card.appendChild(header);

    if (edit.reason) {
      const reasonEl = document.createElement("div");
      reasonEl.className = "edit-card-reason";
      reasonEl.textContent = edit.reason;
      card.appendChild(reasonEl);
    }

    const origBlock = document.createElement("div");
    origBlock.className = "edit-diff edit-diff-remove";
    origBlock.innerHTML = `<span class="diff-label">−</span><span class="diff-text">${_escHtml(_truncate(edit.original_text, 300))}</span>`;
    card.appendChild(origBlock);

    const newBlock = document.createElement("div");
    newBlock.className = "edit-diff edit-diff-add";
    newBlock.innerHTML = `<span class="diff-label">+</span><span class="diff-text">${_escHtml(_truncate(edit.new_text, 300))}</span>`;
    card.appendChild(newBlock);

    const actions = document.createElement("div");
    actions.className = "edit-card-actions";

    const acceptBtn = document.createElement("button");
    acceptBtn.className = "edit-accept-btn";
    acceptBtn.textContent = "Accept";
    acceptBtn.addEventListener("click", () => handleAcceptEdit(edit, card));

    const rejectBtn = document.createElement("button");
    rejectBtn.className = "edit-reject-btn";
    rejectBtn.textContent = "Dismiss";
    rejectBtn.addEventListener("click", () => handleRejectEdit(card));

    actions.appendChild(acceptBtn);
    actions.appendChild(rejectBtn);
    card.appendChild(actions);

    container.appendChild(card);
  });

  const time = aiMsgEl.querySelector(".msg-time");
  if (time) {
    time.parentNode.insertBefore(container, time);
  } else {
    aiMsgEl.appendChild(container);
  }
}

// Accept edit: apply to Word document with tracked changes, update card
async function handleAcceptEdit(edit, cardEl) {
  const editId = `edit_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;

  try {
    cardEl.classList.add("edit-applying");
    cardEl.querySelector(".edit-accept-btn").textContent = "Applying…";
    cardEl.querySelector(".edit-accept-btn").disabled = true;

    await applyEditToDocument(edit.original_text, edit.new_text, edit.node_id);

    appliedEdits.push({
      id: editId,
      nodeId: edit.node_id,
      originalText: edit.original_text,
      newText: edit.new_text,
      reason: edit.reason || "",
      timestamp: new Date().toISOString(),
      undone: false,
    });

    await refreshAfterEdit();

    cardEl.classList.remove("edit-applying");
    cardEl.classList.add("edit-applied");

    const actions = cardEl.querySelector(".edit-card-actions");
    actions.innerHTML = "";

    const appliedLabel = document.createElement("span");
    appliedLabel.className = "edit-applied-label";
    appliedLabel.textContent = "✓ Applied";
    actions.appendChild(appliedLabel);

    const undoBtn = document.createElement("button");
    undoBtn.className = "edit-undo-btn";
    undoBtn.textContent = "Undo";
    undoBtn.addEventListener("click", () => handleUndoEdit(editId, edit, cardEl));
    actions.appendChild(undoBtn);
  } catch (err) {
    // devLog("EDIT_APPLY_ERR", err.message);
    cardEl.classList.remove("edit-applying");
    cardEl.querySelector(".edit-accept-btn").textContent = "Failed — Retry";
    cardEl.querySelector(".edit-accept-btn").disabled = false;
  }
}

function handleRejectEdit(cardEl) {
  cardEl.classList.add("edit-dismissed");
  const actions = cardEl.querySelector(".edit-card-actions");
  actions.innerHTML = '<span class="edit-dismissed-label">Dismissed</span>';
}

// Undo: turn tracking off, find edited text, reject/replace, restore tracking
async function handleUndoEdit(editId, edit, cardEl) {
  try {
    await Word.run(async (context) => {
      const doc = context.document;
      doc.load("changeTrackingMode");
      await context.sync();

      const prevMode = doc.changeTrackingMode;

      doc.changeTrackingMode = Word.ChangeTrackingMode.off;
      await context.sync();

      try {
        const searchStr = edit.new_text.slice(0, 250);
        let best = null;

        // Try paragraph-scoped search first (fast, unambiguous)
        if (edit.node_id) {
          const node = activeNodeMap[edit.node_id];
          const paraIdx = node?.position?.paragraphIndex;
          if (paraIdx != null) {
            const paras = doc.body.paragraphs;
            paras.load("items/text");
            await context.sync();
            if (paraIdx < paras.items.length) {
              const para = paras.items[paraIdx];
              const paraResults = para.search(searchStr, { matchCase: true, matchWholeWord: false });
              paraResults.load("items/text");
              await context.sync();
              if (paraResults.items.length > 0) best = paraResults.items[0];
            }
          }
        }

        // Fallback: full body search
        let results = null;
        if (!best) {
          results = doc.body.search(searchStr, {
            matchCase: true,
            matchWholeWord: false,
          });
          results.load("items/text");
          await context.sync();
        }

        if (!best && results && results.items.length > 0) {
          best = results.items[0];
          if (results.items.length > 1 && edit.new_text.length > 250) {
            for (const item of results.items) {
              if (item.text.trim() === edit.new_text.trim()) {
                best = item;
                break;
              }
            }
          }
        }

        // Fallback: try original_text if new_text not found (tracked changes may still show it)
        if (!best) {
          // devLog("UNDO", "new_text not found, trying original_text as fallback");
          const fallbackStr = edit.original_text.slice(0, 250);
          const fallbackResults = doc.body.search(fallbackStr, {
            matchCase: true,
            matchWholeWord: false,
          });
          fallbackResults.load("items/text");
          await context.sync();

          if (fallbackResults.items.length > 0) {
            best = fallbackResults.items[0];
            try {
              const rangeTracked = best.getTrackedChanges();
              rangeTracked.load("items");
              await context.sync();
              if (rangeTracked.items.length > 0) {
                for (const tc of rangeTracked.items) {
                  tc.reject();
                }
                await context.sync();
              }
            } catch (e) {
              // devLog("UNDO_TC_RANGE", e.message);
            }
            return;
          }

          throw new Error("Could not find the edited text in the document to undo");
        }

        // Accept tracked changes on this range only, then replace with original
        try {
          const rangeTracked = best.getTrackedChanges();
          rangeTracked.load("items");
          await context.sync();
          if (rangeTracked.items.length > 0) {
            for (const tc of rangeTracked.items) {
              tc.accept();
            }
            await context.sync();

            results = doc.body.search(searchStr, {
              matchCase: true,
              matchWholeWord: false,
            });
            results.load("items/text");
            await context.sync();
            best = results.items[0] || best;
          }
        } catch (tcErr) {
          // devLog("UNDO_TC_RANGE", tcErr.message);
        }

        best.insertText(edit.original_text, "Replace");
        await context.sync();
      } finally {
        doc.changeTrackingMode = prevMode;
        await context.sync();
      }
    });

    const tracked = appliedEdits.find((e) => e.id === editId);
    if (tracked) tracked.undone = true;

    await refreshAfterEdit();

    cardEl.classList.remove("edit-applied");
    cardEl.classList.add("edit-undone");

    const actions = cardEl.querySelector(".edit-card-actions");
    actions.innerHTML = '<span class="edit-undone-label">Undone</span>';
  } catch (err) {
    // devLog("EDIT_UNDO_ERR", err.message);
  }
}

// Apply edit with tracked changes: try paragraphIndex first, fallback to global search
async function applyEditToDocument(originalText, newText, nodeId) {
  await Word.run(async (context) => {
    const doc = context.document;
    doc.load("changeTrackingMode");
    await context.sync();

    const prevMode = doc.changeTrackingMode;

    doc.changeTrackingMode = Word.ChangeTrackingMode.trackAll;
    await context.sync();

    try {
      let replaced = false;

      if (nodeId) {
        const node = activeNodeMap[nodeId];
        const paraIdx = node?.position?.paragraphIndex;
        if (paraIdx != null && paraIdx >= 0) {
          const paras = doc.body.paragraphs;
          paras.load("items/text");
          await context.sync();

          if (paraIdx < paras.items.length) {
            const para = paras.items[paraIdx];
            const paraText = (para.text || "").trim();
            const origTrimmed = originalText.trim();

            if (paraText === origTrimmed || paraText.includes(origTrimmed)) {
              const searchResults = para.search(originalText.slice(0, 250), {
                matchCase: true,
                matchWholeWord: false,
              });
              searchResults.load("items/text");
              await context.sync();

              if (searchResults.items.length > 0) {
                searchResults.items[0].insertText(newText, "Replace");
                await context.sync();
                replaced = true;
              }
            }
          }
        }
      }

      if (!replaced) {
        const searchStr = originalText.slice(0, 250);
        const results = doc.body.search(searchStr, {
          matchCase: true,
          matchWholeWord: false,
        });
        results.load("items/text");
        await context.sync();

        if (results.items.length === 0) {
          throw new Error("Could not find the original text in the document");
        }

        let bestMatch = results.items[0];
        if (results.items.length > 1 && originalText.length > 250) {
          for (const item of results.items) {
            if (item.text.trim() === originalText.trim()) {
              bestMatch = item;
              break;
            }
          }
        }

        bestMatch.insertText(newText, "Replace");
        await context.sync();
      }
    } finally {
      doc.changeTrackingMode = prevMode;
      await context.sync();
    }
  });
}

// Re-scan after edit to keep node map, indices, and clause tree in sync
async function refreshAfterEdit() {
  try {
    const scan = await scanDocument();
    applyAndDiffToActive(scan.nodes);
    setLastScan(scan);

    await flushToBackend();

    if (documentId) {
      try {
        const tree = await getDocumentTree(documentId);
        if (tree && tree.structure && tree.structure.length > 0) {
          setCachedTree(tree.structure);
          saveToSettings("miai_tree", tree.structure);
          renderClauseTree(tree.structure);
        }
      } catch (treeErr) {
        // devLog("REFRESH_TREE_ERR", treeErr.message);
      }
    }

    // devLog("REFRESH", `Re-scanned: ${scan.nodes.length} nodes, flushed to backend`);
  } catch (err) {
    // devLog("REFRESH_ERR", err.message);
  }
}

